import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ThumbsUp, ThumbsDown, Question, MapPin, NotePencil } from "@phosphor-icons/react";
import { SEVERITY_STYLE } from "./FraudScoreBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEV_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };
const ACTIONS = {
  confirmed: { label: "Confirm", icon: ThumbsUp, cls: "text-green-700 hover:bg-green-50", prompt: "Confirm this alert — add a note for the audit trail (optional)" },
  false_positive: { label: "False positive", icon: ThumbsDown, cls: "text-gray-600 hover:bg-gray-100", prompt: "Why is this a false positive? (optional, recommended)" },
  needs_review: { label: "Needs review", icon: Question, cls: "text-yellow-700 hover:bg-yellow-50", prompt: "What needs to be checked? (optional)" },
};

function AlertRow({ alert, claimId, onSelectPage, onFeedback }) {
  const [pending, setPending] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const fb = alert.feedback;
  const muted = fb === "false_positive";
  const tid = `${alert.alert_code}-${alert.page_no || "doc"}-${alert.alert_id.slice(0, 4)}`;

  const send = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/fraud/alerts/feedback`, {
        claim_id: claimId, alert_key: alert.alert_key, alert_code: alert.alert_code, page_no: alert.page_no, action: pending, notes: notes.trim()
      });
      toast.success(`Marked ${pending.replace("_", " ")} — score now ${res.data.composite_score}`);
      setPending(null); setNotes("");
      onFeedback();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save feedback");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`border rounded-md px-3 py-2.5 ${muted ? "opacity-60 border-gray-200 bg-gray-50" : alert.severity === "critical" ? "border-red-300 bg-red-50" : "border-gray-200 bg-white"}`}
         data-testid={`fraud-alert-${tid}`}>
      <div className="flex items-start gap-2">
        <Badge variant="outline" className={`text-[10px] font-bold flex-shrink-0 ${SEVERITY_STYLE[alert.severity] || SEVERITY_STYLE.low}`}>
          {(alert.severity || "").toUpperCase()}
        </Badge>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-mono text-gray-400">{alert.alert_code}</span>
            <span className="text-sm font-semibold text-gray-900">{alert.name}</span>
            {alert.page_no && (
              <button onClick={() => onSelectPage?.(alert.page_no)} className="text-[11px] text-[#002FA7] hover:underline flex items-center gap-0.5" data-testid={`alert-page-link-${tid}`}>
                <MapPin size={11} /> Page {alert.page_no}{alert.bounding_box ? " · boxed" : ""}
              </button>
            )}
            {fb && <Badge variant="outline" className="text-[10px] capitalize">{fb.replace("_", " ")}</Badge>}
            <span className="ml-auto text-[10px] text-gray-400">{alert.points ?? 0} pts</span>
          </div>
          <p className={`text-xs mt-0.5 ${muted ? "line-through text-gray-400" : "text-gray-700"}`}>{alert.evidence_text}</p>
          {alert.feedback_notes && (
            <p className="text-[11px] text-gray-600 mt-1 flex items-start gap-1 italic" data-testid={`alert-note-${tid}`}>
              <NotePencil size={12} className="mt-0.5 flex-shrink-0" /> “{alert.feedback_notes}” <span className="not-italic text-gray-400">— {alert.feedback_by}</span>
            </p>
          )}
        </div>
      </div>

      {pending ? (
        <div className="mt-2 border-t border-gray-200 pt-2" data-testid={`note-form-${tid}`}>
          <p className="text-[11px] text-gray-500 mb-1">{ACTIONS[pending].prompt}</p>
          <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="text-xs min-h-0" placeholder="Investigator note…" data-testid={`note-input-${tid}`} />
          <div className="flex gap-1 mt-1.5 justify-end">
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => { setPending(null); setNotes(""); }} className="h-6 px-2 text-[11px]" data-testid={`note-cancel-${tid}`}>Cancel</Button>
            <Button size="sm" disabled={busy} onClick={send} className="h-6 px-3 text-[11px] bg-[#002FA7] text-white" data-testid={`note-save-${tid}`}>
              Save as {ACTIONS[pending].label.toLowerCase()}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex gap-1 mt-2 justify-end">
          {Object.entries(ACTIONS).map(([k, a]) => {
            const I = a.icon;
            const tidKey = k === "confirmed" ? "confirm" : k === "false_positive" ? "false" : "review";
            return (
              <Button key={k} size="sm" variant="ghost" disabled={busy} onClick={() => setPending(k)} className={`h-6 px-2 text-[11px] ${a.cls}`} data-testid={`fb-${tidKey}-${tid}`}>
                <I size={12} className="mr-1" /> {a.label}
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function FraudAlertsList({ alerts, claimId, onSelectPage, onFeedback }) {
  const [filter, setFilter] = useState("all");
  const sorted = [...(alerts || [])].sort((a, b) => (SEV_ORDER[a.severity] ?? 9) - (SEV_ORDER[b.severity] ?? 9));
  const categories = ["all", ...Array.from(new Set(sorted.map(a => a.category)))];
  const shown = filter === "all" ? sorted : sorted.filter(a => a.category === filter);

  if (!sorted.length) {
    return (
      <div className="border border-green-200 bg-green-50 rounded-md p-5 text-center" data-testid="fraud-no-alerts">
        <p className="text-sm font-medium text-green-800">No fraud alerts triggered</p>
        <p className="text-xs text-green-600 mt-1">All consistency, arithmetic, pricing, clinical and forensic rules passed.</p>
      </div>
    );
  }

  return (
    <div data-testid="fraud-alerts-list">
      <div className="flex gap-1 flex-wrap mb-3">
        {categories.map(c => (
          <button key={c} onClick={() => setFilter(c)}
                  className={`text-[11px] px-2.5 py-1 rounded-full border capitalize transition-colors ${filter === c ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}
                  data-testid={`alert-filter-${c}`}>
            {c} {c !== "all" && `(${sorted.filter(a => a.category === c).length})`}
          </button>
        ))}
      </div>
      <div className="space-y-2">
        {shown.map(a => <AlertRow key={a.alert_id} alert={a} claimId={claimId} onSelectPage={onSelectPage} onFeedback={onFeedback} />)}
      </div>
    </div>
  );
}
